import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { useCallback, useState } from "react";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";
import { processError } from "../../../../common/utils/base_error";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useCreateDebounceHandler, useDebouncedCallback } from "../../../../common/hooks/use_debounced_callback";
import { evaluateParamValue } from "../../../../common/utils/params_utils";

import { useReportViewer } from "../../../../config/hooks/utils/report_viewer";
import { useNavigate, useParams } from "react-router";
import { CentroCustoDropDown } from "../../../../common/domain/models/centro_custo_dropdown";
import { useDropDownLoader } from "../../../../common/hooks/use_dropdown_loader";
import { LogTipoTabela } from "../../../../common/utils/log_tabela_tipo";
import { useChangeHistory } from "../../../../common/hooks/use_change_history";
import { DropDown } from "../../../../common/domain/models/dropdown";
import { ControleEstoqueForm, controleEstoqueSchema } from "../schemas/Controle_Estoque/controle_estoque";
import { ControleEstoque } from "../../domain/models/controle_estoque_lista";
import { useControleEstoqueItemFilters } from "../hooks/controleEstoque_item/filter";
import { useControleEstoqueItemPagination } from "../hooks/controleEstoque_item/pagination";
import { ItemControleEstoque } from "../../domain/models/item_controle_estoque_lista";
import { GridRowId } from "@mui/x-data-grid";
import { ItemControleEstoqueForm } from "../schemas/Item_Controle/controle_estoque";
import { useUserAuthStore } from "../../../../stores/global/userAuth";

type LoadEstoqueListaFuncState = {
  IdControleEstoque?: number;
  ProdutoPesquisa?: number,
  page?: number;
  rowsPerPage?: number;
};

export const useControleEstoqueCadastroViewModel = () => {
  const {
    commons: { getAllLogAlteracaoUseCase, getAllLogAlteracaoByParentUseCase, getProdutoServicoEsDropDownUseCase },
    reports: {getReportControleEstoqueUseCase},
    controleEstoque: { getAllListagemItemControleUseCase, deleteItemControleUseCase, createItemControleUseCase, updateItemControleUseCase },
  } = useDependencyInjection();

  
  const { idUsuario } = useUserAuthStore();
  const {
    loadCentroCustoDropDown,
  } = useDropDownLoader();

  const hookForm = useForm<ControleEstoqueForm>({
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    resolver: zodResolver(controleEstoqueSchema),
    defaultValues: {
      
    }
  });
  //#region Relatorio
  const [reportViewerState, startReportViewerProcess] = useReportViewer();
  //#endregion
  const changeHistory = useChangeHistory({
    getAllLogAlteracaoByParentUseCase: getAllLogAlteracaoByParentUseCase,
    getAllLogAlteracaoUseCase: getAllLogAlteracaoUseCase,
    onSetError: (e) => pushErrorNotification("Erro", [e]),
  });
  const navigate = useNavigate();
  const { id } = useParams();

  const [loading, setLoading] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [itemControleModal, setItemControleModal] = useState<boolean>(false);
  const [deleteModal, setDeleteModal] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>();
  const [itemControleLista, setItemControleLista] = useState<ItemControleEstoque[]>([]);
  const [selectedItemControle, setSelectedItemControle] = useState<ItemControleEstoque>();
  const [selectedProdutosServicosQuery, setSelectedProdutosServicosQuery] = useState<DropDown[] | null>(null);
  const [produtosServicosDropDown, setProdutosServicosDropDown] = useState<DropDown[]>();

  const {pushErrorNotification, setCustomLoading, pushInfoNotification, pushWarnNotification } = usePageLoader();
  const debouncedRef = useCreateDebounceHandler(750);

  const filters = useControleEstoqueItemFilters();
  const pagination = useControleEstoqueItemPagination();
  

  // #region Funções de carregamento

  const loadEstoqueListaState = useCallback(async (funcState?: LoadEstoqueListaFuncState) => {
      try {
        const filterID_IdControleEstoque = evaluateParamValue(funcState?.IdControleEstoque, filters.IdControleEstoque);
        const filteredPesquisa = funcState?.ProdutoPesquisa ?? filters.ProdutoPesquisa;
        
        const filteredPage = funcState?.page ?? pagination.page;
        const filteredRowsPerPage = funcState?.rowsPerPage ?? pagination.rowsPerPage;
        if(filterID_IdControleEstoque){
          await getAllListagemItemControleUseCase.getAllListagemItemControle({
                IdControleEstoque: filterID_IdControleEstoque,IdProdutoServico: filteredPesquisa
              },
              filteredPage,
              filteredRowsPerPage
          );
          setItemControleLista(getAllListagemItemControleUseCase.items);
          pagination.setRowsPerPage(getAllListagemItemControleUseCase.pageSize);
          pagination.setTotalItems(getAllListagemItemControleUseCase.totalItems);
        }
      } catch (e: unknown) {
          throw new Error("Falha ao buscar Itens de estoque!");
      }
  }, [filters.IdControleEstoque, filters.ProdutoPesquisa, pagination, getAllListagemItemControleUseCase]);

  const loadProdutosServicosDropdown = useCallback(async () => {
    try {
      if(id){
        const results = await getProdutoServicoEsDropDownUseCase
          .getProdutoServicoEsDropDown(Number(id));
        setProdutosServicosDropDown(results);
      }
    } catch (e: unknown) {
      setProdutosServicosDropDown(undefined);
      throw new Error('Falha ao buscar produtos/serviços!');
    }
  }, [getProdutoServicoEsDropDownUseCase, id]);
  
 

  //#endregion

  // #region useEffect
  usePageStartBootstrap(async () => {
    try {
      const jobs = [];
      if(id) jobs.push(loadEstoqueListaState({IdControleEstoque: Number(id)}))

      jobs.push(loadProdutosServicosDropdown());
      
        await Promise.all(jobs);
    } catch (e: unknown) {
      setErrorMessage(processError(e, 'Falha ao buscar dados da pagina!')[0]);
    }
  });

  const handleReload = useCallback(async () => {
    try {
      const jobs = [];
      setLoading(true);
      if(id) jobs.push(loadEstoqueListaState({IdControleEstoque: Number(id)}))

      jobs.push(loadProdutosServicosDropdown());
      
      await Promise.all(jobs);
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
    } finally {
      setLoading(false);
    }
  }, [id, loadEstoqueListaState, loadProdutosServicosDropdown, pushErrorNotification]);
  //#endregion

  // #region Eventos
  const handlePageChange = useCallback(async (page: number) => {
    try {
      setLoading(true);
      pagination.setPage(page + 1);
      await loadEstoqueListaState({page: page + 1,});
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
    } finally {
      setLoading(false);
    }
  }, [loadEstoqueListaState, pagination, pushErrorNotification]);

  
  const handleRowsPerPageChange = useCallback(async (rowsPerPage: number) => {
    try {
          setLoading(true);
          pagination.setPage(1);
          pagination.setRowsPerPage(rowsPerPage);
    
          await loadEstoqueListaState({ page: 1, rowsPerPage });
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
    } finally {
      setLoading(false);
    }
  }, [loadEstoqueListaState, pagination, pushErrorNotification]);


  const handleCadastroControleEstoqueOpenState = useCallback(( entradaModal: boolean) => {
      setItemControleModal(entradaModal);
      setSelectedItemControle(undefined);
      setSelectedProdutosServicosQuery(null);
    
  }, []);
 
  const handleDeleteModalOpenState = useCallback((Id: number | null, openState: boolean) => {
    if (openState) {
      if (Id && itemControleLista) {
        setSelectedItemControle(itemControleLista.find(x => x.IdItemControleEstoque === Id));
      } else {
        setSelectedItemControle(undefined);
      }
    }
    setDeleteModal(openState);
  }, [itemControleLista]);

  const handleDeleteConfirma = useCallback(async () => {
    if (selectedItemControle) {
      try {
        setSaving(true);
        await deleteItemControleUseCase.deleteItemControle(selectedItemControle.IdItemControleEstoque);
        await loadEstoqueListaState({IdControleEstoque: Number(id)});
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, 'Falha ao excluir produto!'));
      } finally {
        setSaving(false);
        setDeleteModal(false);
      }
    }
  }, [deleteItemControleUseCase, id, loadEstoqueListaState, pushErrorNotification, selectedItemControle]);

  const handleControleEstoqueSubmit = useCallback(async () => {
    try {
      setSaving(true);
      const selectedProdutoServicoIds = selectedProdutosServicosQuery?.map(x => x.id) ?? null;

      await createItemControleUseCase.createItemControle(selectedProdutoServicoIds ?? null, Number(id));
        pushInfoNotification("Sucesso", "Criado com sucesso!", { isAutoFinish: true });
        await loadEstoqueListaState({IdControleEstoque: Number(id)});
      
    } catch (e) {
        pushErrorNotification("Erro", processError(e, 'Falha ao criar/atualizar Controle Estoque!'));
    }finally {
      setSaving(false);
      setItemControleModal(false);
    }
  },[createItemControleUseCase, id, loadEstoqueListaState, pushErrorNotification, pushInfoNotification, selectedProdutosServicosQuery]);

   const handleUpdateMutiTodos = async () => {
    try {
        setSaving(true);
        const payload = itemControleLista; 
        if(payload.length === 0) return pushWarnNotification("Atenção","Nenhum produto selecionado para atualização!"); 
        await updateItemControleUseCase.updateItemControle(payload);
        
        pushInfoNotification("Sucesso", "Produtos atualizados com sucesso!", { isAutoFinish: true });
        await loadEstoqueListaState({IdControleEstoque: Number(id)});
        
      } catch (e) {
        pushErrorNotification("Erro", "Falha ao atualizar os Produtos.");
      } finally {
        setSelectedProdutosServicosQuery(null);
        setSaving(false);
      }
  };


  //#endregion


  //#region Filtros
  const handleClearFilters = useCallback(async () => {
      try {
          setLoading(true);
          filters.clearFilters();
          pagination.setPage(1);
          await loadEstoqueListaState({ IdControleEstoque: Number(id) });
      } catch (e: unknown) {
          pushErrorNotification("Erro", processError(e, 'Falha ao buscar produtos!'));
      } finally {
          setLoading(false);
      }
  }, [filters, id, loadEstoqueListaState, pagination, pushErrorNotification]);

  const handlePesquisaChange = useDebouncedCallback( debouncedRef,  (newValue: number | null) => {
    if (newValue) {
        useControleEstoqueItemFilters.getState().setProdutoPesquisa(newValue);
        return async () => {
          try {
            setLoading(true);
            useControleEstoqueItemPagination.getState().setPage(1);
            await loadEstoqueListaState({ IdControleEstoque: Number(id),ProdutoPesquisa: newValue, page: 1 });
          } catch (e: unknown) {
            pushErrorNotification("Erro", processError(e, "Falha ao carregar pesquisar!"));
          } finally {
            setLoading(false);
          }
        }
      } else {
        return async () => {
          try {
            setLoading(true);
            useControleEstoqueItemPagination.getState().setPage(1);
            await loadEstoqueListaState(); 
          } catch (e: unknown) {
            pushErrorNotification("Erro", processError(e, "Falha ao carregar pesquisa!"));
          } finally {
            setLoading(false);
          }
        }
      }
    }, 
    [ loadEstoqueListaState, pushErrorNotification, setLoading]
  );
  //#endregion

  
  //#region Relatoria
  const handleSubmitReport = useCallback(async () => {
    try {
      setSaving(true);
      await startReportViewerProcess({
        fetchCallback: async (requiredMimeType) =>
          await getReportControleEstoqueUseCase.getReportControleEstoque(
            requiredMimeType,
            Number(id),
            idUsuario
          ),
      });
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao gerar relatorio"));
    } finally {
      setSaving(false);
    }
  }, [getReportControleEstoqueUseCase, id, idUsuario, pushErrorNotification, startReportViewerProcess]);


  const handleOpenChangeHistory = useCallback(
    () => {
      if(id) changeHistory.handleOpenChangeHistory(Number(id), LogTipoTabela.CONTROLE_ESTOQUE, true);
      
    },
    [changeHistory, id],
  );

  //#endregion
  

  const handleQtdeEstoqueMinChange = (novaQtd: number | null, idLinhaGrid: GridRowId) => {
    setItemControleLista((listaAtual) => 
        listaAtual.map((item) => 
            item.IdItemControleEstoque === idLinhaGrid 
                ? { ...item, QtdeEstoqueMin: novaQtd } 
                : item
        )
    );
  };
  const handleQtdeEstoqueMaxChange = (novaQtd: number | null, idLinhaGrid: GridRowId) => {
    setItemControleLista((listaAtual) => 
        listaAtual.map((item) => 
            item.IdItemControleEstoque === idLinhaGrid 
                ? { ...item, QtdeEstoqueMax: novaQtd } 
                : item
        )
    );
  };
  const handleLocalArmazenamentoChange = (novoLocal: string | null, idLinhaGrid: GridRowId) => {
    setItemControleLista((listaAtual) => 
        listaAtual.map((item) => 
            item.IdItemControleEstoque === idLinhaGrid 
                ? { ...item, LocalArmazenamento: novoLocal } 
                : item
        )
    );
  };
  
   const handleProdutosServicosQueryChange = useCallback((items: DropDown[] | null) => {
    setSelectedProdutosServicosQuery(items);
  }, []);

  return {
    loading,
    saving,
    errorMessage,
    reportViewerState,
    pagination,
    filters,
    changeHistory,
    hookForm,
    deleteModal,
    itemControleModal,
    itemControleLista,
    selectedItemControle,
    selectedProdutosServicosQuery,
    produtosServicosDropDown,
    setItemControleModal,
    handleLocalArmazenamentoChange,
    handleQtdeEstoqueMinChange,
    handleQtdeEstoqueMaxChange,
    handleReload,
    handleRowsPerPageChange,
    handlePageChange,
    handleDeleteConfirma,
    handleDeleteModalOpenState,
    handleClearFilters,
    handleSubmitReport,
    handlePesquisaChange,
    handleOpenChangeHistory,
    handleCadastroControleEstoqueOpenState,
    handleControleEstoqueSubmit,
    handleProdutosServicosQueryChange,
    handleUpdateMutiTodos
      
  }
}